import { create } from 'zustand';

/**
 * Backtest / Equity Store (GEMIC-compatible)
 * ------------------------------------------
 * Responsibilities:
 * 1. Normalize WS payload ONCE
 * 2. Enforce stable frontend data contract
 * 3. Calculate running equity from trade PnL
 * 4. Do NOT split backtest/live
 *
 * Source table:
 * everyday_ic_trade_log
 *
 * Final row shape GUARANTEED:
 * {
 *   id: number | null,
 *   date: 'YYYY-MM-DD',
 *   equity: number,
 *   daily_pnl: number | null,
 *   daily_return: number | null,
 *   source: 'BACKTEST' | 'LIVE',
 *
 *   // GEMIC trade fields kept for dashboard/table use
 *   symbol: string | null,
 *   run_eval_day: string | null,
 *   expiration_day: string | null,
 *   short_call: number | null,
 *   long_call: number | null,
 *   short_put: number | null,
 *   long_put: number | null,
 *   entry_credit: number | null,
 *   contracts: number | null,
 *   width_points: number | null,
 *   status: string | null,
 *   outcome: string | null,
 *   closing_price: number | null,
 *   opened_at: string | null,
 *   closed_at: string | null,
 *   updated_at: string | null
 * }
 */

const STARTING_EQUITY = 10000;

function toIsoDate(value) {
  if (!value) return null;

  const str = String(value);

  // Already ISO-like: 2026-05-05 or 2026-05-05T20:00:03Z
  if (str.includes('-')) {
    return str.slice(0, 10);
  }

  // Handles MM/DD/YY or MM/DD/YYYY
  if (str.includes('/')) {
    const [m, d, y] = str.split('/');
    const yyyy = y.length === 2 ? `20${y}` : y;

    return `${yyyy}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  return str.slice(0, 10);
}

function getSortDate(row) {
  return (
    row.closed_at ||
    row.expiration_day ||
    row.date ||
    row.run_eval_day ||
    row.opened_at ||
    ''
  );
}

function normalizeSource(row) {
  return row.source || row.trade_source || null;
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === '') return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeRow(row, runningEquity, previousEquity) {
  if (!row) return null;

  const pnl =
    normalizeNumber(row.daily_pnl) ??
    normalizeNumber(row.pnl) ??
    0;

  const date = toIsoDate(
    row.date ||
    row.closed_at ||
    row.expiration_day ||
    row.run_eval_day ||
    row.opened_at
  );

  const dailyReturn =
    previousEquity && Number.isFinite(previousEquity)
      ? pnl / previousEquity
      : null;

  return {
    id: row.id ?? null,
    date,
    equity: runningEquity,
    daily_pnl: pnl,
    daily_return: dailyReturn,
    source: normalizeSource(row),

    // GEMIC trade fields
    symbol: row.symbol ?? null,
    run_eval_day: toIsoDate(row.run_eval_day),
    expiration_day: toIsoDate(row.expiration_day),
    short_call: normalizeNumber(row.short_call),
    long_call: normalizeNumber(row.long_call),
    short_put: normalizeNumber(row.short_put),
    long_put: normalizeNumber(row.long_put),
    entry_credit: normalizeNumber(row.entry_credit),
    contracts: normalizeNumber(row.contracts),
    width_points: normalizeNumber(row.width_points),
    status: row.status ?? null,
    outcome: row.outcome ?? null,
    closing_price: normalizeNumber(row.closing_price),
    opened_at: row.opened_at ?? null,
    closed_at: row.closed_at ?? null,
    updated_at: row.updated_at ?? null
  };
}

function buildEquitySeries(rows) {
  if (!Array.isArray(rows)) return [];

  const sorted = [...rows].sort((a, b) => {
    const aTime = new Date(getSortDate(a)).getTime();
    const bTime = new Date(getSortDate(b)).getTime();

    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;

    return aTime - bTime;
  });

  let runningEquity = STARTING_EQUITY;

  return sorted
    .map((row) => {
      const previousEquity = runningEquity;

      const pnl =
        normalizeNumber(row.daily_pnl) ??
        normalizeNumber(row.pnl) ??
        0;

      runningEquity += pnl;

      return normalizeRow(row, runningEquity, previousEquity);
    })
    .filter(Boolean);
}

const useBacktestStore = create((set) => ({
  startingEquity: STARTING_EQUITY,
  equitySeries: [],

  /**
   * WS is authoritative.
   * Store converts raw GEMIC trade rows into chart-ready equity rows.
   */
  setEquitySeries: (rows) =>
    set({
      equitySeries: buildEquitySeries(rows)
    }),

  clearEquitySeries: () =>
    set({
      equitySeries: []
    })
}));

export default useBacktestStore;

